import { useState } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";

import { getAccountsForFund } from "../../../services/fund.service";
import { createGlEntries } from "../../../services/workbook.service";
import { useEffectAsync } from "../../../utils/hooks/useEffectAsync.hook";
import { GlMapping } from "../../../utils/types/fund.type";
import { GLEntryV2, PostingType } from "../../../utils/types/workbook.type";

type Transaction = {
  id: string | null;
  index: number;
  account: string;
  debit: string;
  credit: string;
  description: string;
  name: string;
};

const defaultTransaction: Transaction = {
  id: null,
  index: 1,
  account: "",
  debit: "",
  credit: "",
  description: "",
  name: "",
};

type FormValues = {
  date: Date;
  adjustingEntry: boolean;
  memo: string;
  transactions: Transaction[]
};

type Props = {
  fundId?: string;
  currency: string;
  closeDrawer: Function;
  informationAlert: Function;
};

export const useJournalEntries = ({
  fundId,
  currency,
  informationAlert,
  closeDrawer,
}: Props) => {
  const [performDelete, setPerformDelete] = useState<boolean>(false);
  const [glAccounts, setGLAccounts] = useState<any[]>([]);
  const { control, getValues, handleSubmit, watch, reset } = useForm<FormValues>({
    defaultValues: {
      date: new Date(),
      adjustingEntry: false,
      memo: "",
      transactions: [defaultTransaction]
    }
  });
  const { fields, append, prepend, remove, insert } = useFieldArray({
    control,
    name: "transactions",
  });

  const formValues = useWatch({
    name: "transactions",
    control
  });

  const handleDelete = (id: string, index: number) => {
    remove(index);
  };

  const handleAddTxLine = () => {
    const line = {
      ...defaultTransaction,
      index: getValues().transactions.length + 1
    };
    
    append(line);
  };

  const handleDeleteAll = () => {
    remove();
    setPerformDelete(false);

    const line = {
      ...defaultTransaction,
      index: 1,
    };

    append(line);
  };

  const popupDeleteConfirmationBox = () => {
    setPerformDelete(true);
  };

  const handleCancel = () => setPerformDelete(false);

  const handleSumbitJournal = (data: FormValues) => {
    if(fundId) {
      const params: GLEntryV2 = {
        date: data.date,
        adjustingEntry: data.adjustingEntry,
        memo: data.memo,
        lineItems: data.transactions.map((transaction: Transaction) => ({
          name: transaction.name,
          accountId: transaction.account,
          amount: Number(!isNaN(Number(transaction.credit)) && transaction.credit !== "" && transaction.credit !== null ? transaction.credit: transaction.debit),
          postingType: !isNaN(Number(transaction.credit)) && transaction.credit !== "" && transaction.credit !== null ? PostingType.CREDIT: PostingType.DEBIT,
          description: transaction.description,
          currency,
        }))
      };
      
      createGlEntries(fundId, params)
        .then(res => {
          reset();
          closeDrawer();
        })
        .catch(err => informationAlert("Error on adding lines in journal entries.", "error"));
    }
  };

  const totalDebit = formValues.reduce(
    (acc, current) => acc + (Number(current.debit) || 0),
    0
  );

  const totalCredit = formValues.reduce(
    (acc, current) => acc + (Number(current.credit) || 0),
    0
  );

  useEffectAsync(async (isCanceled) => {
    if(fundId) {
      const glAccounts: GlMapping = await getAccountsForFund(fundId);

      setGLAccounts(glAccounts.accounts ?? []);
    }
    if(isCanceled()) return;

  }, [fundId]);

  return {
    glAccounts,
    formValues,
    performDelete,
    fields,
    control,
    totalCredit,
    totalDebit,
    handleAddTxLine,
    handleCancel,
    handleDelete,
    handleDeleteAll,
    handleSumbitJournal,
    handleSubmit,
    popupDeleteConfirmationBox,
  };
};